import { useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import logo from "../../assets/images/RedP_LogoT.png";
import "./Downloads.css";

const BIG3_DOWNLOAD_URL = "https://github.com/calebcoomer/waterview-app-updates/releases/download/release/Big3-Agent-1.0.0-macOS-arm64.dmg";

const SourceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21h18" />
    <path d="M5 21V8l7-4 7 4v13" />
    <path d="M9 21v-6h6v6" />
  </svg>
);

const Downloads = () => {
  const { user } = useContext(AuthContext);
  const redpPath = user?.id ? `/redp/${user.id}` : "/";
  const downloadBig3 = () => window.location.assign(BIG3_DOWNLOAD_URL);

  return (
    <main className="downloads-page">
    <header className="downloads-header">
      <Link to={redpPath} className="downloads-brand" aria-label={user ? "Back to RedP" : "Go to RedP sign in"}>
        <img src={logo} alt="RedP" />
      </Link>
      <Link to={redpPath} className="downloads-nav-link">
        {user ? "← Back to RedP" : "← Go to sign in"}
      </Link>
    </header>

    <section className="downloads-hero">
      <span className="downloads-eyebrow">RedP Tools</span>
      <h1>Less searching. More time for the work that matters.</h1>
      <p>Purpose-built tools that streamline repetitive real estate research and document collection.</p>
    </section>

    <section className="tool-release-card" aria-labelledby="big-3-title">
      <div className="tool-release-main">
        <h2 id="big-3-title">Big 3</h2>
        <p className="tool-description">
          Automatically collects property documents from the Property Appraiser,
          Tax Collector, and Clerk of Courts.
        </p>

        <div className="source-grid">
          {[
            ["Property Appraiser", "Property records in one streamlined workflow."],
            ["Tax Collector", "Tax information without repetitive searching."],
            ["Clerk of Courts", "Court records gathered alongside property data."],
          ].map(([title, description]) => (
            <article className="source-card" key={title}>
              <SourceIcon />
              <div><h3>{title}</h3><p>{description}</p></div>
            </article>
          ))}
        </div>
      </div>

      <aside className="release-panel">
        <span>macOS release</span>
        <h3>Big 3 is ready.</h3>
        <p>Download Big 3 Agent 1.0.0 for Apple silicon Macs.</p>
        <button className="release-download-button" type="button" onClick={downloadBig3}>
          Download for macOS
        </button>
        <p className="release-coming-soon">Windows version coming soon.</p>
      </aside>
    </section>

    <section className="setup-guide" aria-labelledby="setup-guide-title">
      <div className="setup-guide-intro">
        <span className="setup-guide-eyebrow">First-time setup</span>
        <h2 id="setup-guide-title">Install once. Start searching in minutes.</h2>
        <p>
          Big 3 is not currently signed with an Apple Developer ID or notarized
          by Apple. macOS will block the first launch, so you will need to approve
          the app once in Privacy &amp; Security.
        </p>
      </div>

      <details className="setup-video">
        <summary>
          <span className="setup-video-summary">
            <span>Need help?</span>
            <strong>Watch the first-time setup video</strong>
          </span>
          <span className="setup-video-toggle" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </summary>
        <div className="setup-video-content">
          <video
            controls
            playsInline
            preload="metadata"
            width="1268"
            height="1106"
            aria-label="Big 3 first-time setup video"
          >
            <source src="/videos/howtoinstall.mp4" type="video/mp4" />
            Your browser does not support embedded video.
          </video>
        </div>
      </details>

      <div className="setup-guide-grid">
        <article className="setup-guide-card">
          <h3>Install and approve Big 3</h3>
          <ol className="setup-steps">
            <li>
              <span aria-hidden="true">1</span>
              <div><strong>Install the app</strong><p>Open the DMG and drag Big 3 Agent into Applications.</p></div>
            </li>
            <li>
              <span aria-hidden="true">2</span>
              <div><strong>Try to open it once</strong><p>Double-click Big 3 Agent, then dismiss the macOS security warning.</p></div>
            </li>
            <li>
              <span aria-hidden="true">3</span>
              <div><strong>Allow the app</strong><p>Open System Settings → Privacy &amp; Security, scroll to Security, and click Open Anyway.</p></div>
            </li>
            <li>
              <span aria-hidden="true">4</span>
              <div><strong>Confirm the launch</strong><p>Authenticate with your Mac password or Touch ID, then click Open.</p></div>
            </li>
          </ol>
          <p className="setup-note">
            Open Anyway appears only after the first launch attempt and is
            available for about one hour. Only approve a copy downloaded from
            this page.
          </p>
        </article>

        <article className="setup-guide-card">
          <h3>Run your first property search</h3>
          <ol className="setup-steps">
            <li>
              <span aria-hidden="true">1</span>
              <div><strong>Sign in</strong><p>Use the same account you use for RedP.</p></div>
            </li>
            <li>
              <span aria-hidden="true">2</span>
              <div><strong>Choose a county</strong><p>Select a supported county and enter an owner, address, parcel, or other available search value.</p></div>
            </li>
            <li>
              <span aria-hidden="true">3</span>
              <div><strong>Start the search</strong><p>Choose Run for the full workflow or Quick run for a reduced search.</p></div>
            </li>
            <li>
              <span aria-hidden="true">4</span>
              <div><strong>Open your results</strong><p>Track progress in the queue and find collected files in Documents → Big3 Agent Output.</p></div>
            </li>
          </ol>
        </article>
      </div>

      <a
        className="setup-help-link"
        href="https://support.apple.com/guide/mac-help/open-a-mac-app-from-an-unknown-developer-mh40616/mac"
        target="_blank"
        rel="noreferrer"
      >
        View Apple&apos;s security instructions ↗
      </a>
    </section>
    </main>
  );
};

export default Downloads;
